import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  clearPageFocus,
  markOverlayHandledEscape,
  shouldClearPageFocusOnEscape,
} from "@/utils/keyboardUtils";
import {
  buildShortcutHelp,
  detectOpenShortcutDialog,
  isShortcutHelpEnabled,
  isShortcutHelpToggleKey,
} from "@/utils/shortcutHelp";

const ShortcutHelpContext = createContext(null);

export function useShortcutHelp() {
  return useContext(ShortcutHelpContext);
}

/** Tell the cheat sheet which room you are in (Timeline vs Tasks, etc.). */
export function useShortcutHelpRoom(room) {
  const ctx = useShortcutHelp();
  const setRoom = ctx?.setRoom;
  useEffect(() => {
    if (!setRoom) return undefined;
    setRoom(room || null);
    return () => setRoom(null);
  }, [setRoom, room]);
}

export function ShortcutHelpProvider({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [room, setRoom] = useState(null);
  const [dialogStack, setDialogStack] = useState([]);
  const [fallbackDialogId, setFallbackDialogId] = useState(null);

  useEffect(() => {
    setOpen(false);
    setRoom(null);
    setFallbackDialogId(null);
  }, [location.pathname]);

  const pushDialog = useCallback((id) => {
    setDialogStack((stack) => [...stack, id]);
  }, []);

  const popDialog = useCallback((id) => {
    setDialogStack((stack) => {
      const i = stack.lastIndexOf(id);
      if (i < 0) return stack;
      return stack.filter((_, idx) => idx !== i);
    });
  }, []);

  const setOpenSafe = useCallback((next) => {
    if (next) {
      setFallbackDialogId(detectOpenShortcutDialog());
      setOpen(true);
    } else {
      markOverlayHandledEscape();
      setFallbackDialogId(null);
      setOpen(false);
    }
  }, []);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (prev) {
        markOverlayHandledEscape();
        setFallbackDialogId(null);
        return false;
      }
      setFallbackDialogId(detectOpenShortcutDialog());
      return true;
    });
  }, []);

  const registeredDialogId = dialogStack[dialogStack.length - 1] || null;

  const value = useMemo(
    () => ({
      open,
      setOpen: setOpenSafe,
      toggle,
      room,
      setRoom,
      pushDialog,
      popDialog,
      dialogId: registeredDialogId || fallbackDialogId,
      pathname: location.pathname,
    }),
    [
      open,
      setOpenSafe,
      toggle,
      room,
      pushDialog,
      popDialog,
      registeredDialogId,
      fallbackDialogId,
      location.pathname,
    ]
  );

  return (
    <ShortcutHelpContext.Provider value={value}>
      {children}
      <FocusClearHost />
      <ShortcutHelpHost />
    </ShortcutHelpContext.Provider>
  );
}

/** Esc unfocuses the way a click on empty page background would. */
function FocusClearHost() {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!shouldClearPageFocusOnEscape(e)) return;
      if (!clearPageFocus()) return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  return null;
}

/** Tell the cheat sheet a named window is open (clock editor, calculator, etc.). */
export function useShortcutHelpDialog(dialogId, active = true) {
  const ctx = useShortcutHelp();
  const pushDialog = ctx?.pushDialog;
  const popDialog = ctx?.popDialog;
  useEffect(() => {
    if (!pushDialog || !popDialog || !active || !dialogId) return undefined;
    pushDialog(dialogId);
    return () => popDialog(dialogId);
  }, [pushDialog, popDialog, dialogId, active]);
}

function ShortcutHelpHost() {
  const ctx = useShortcutHelp();
  const enabled = isShortcutHelpEnabled(ctx?.pathname);

  useEffect(() => {
    if (!ctx) return undefined;

    const onKeyDown = (e) => {
      if (!isShortcutHelpEnabled(ctx.pathname)) return;

      if (ctx.open && e.key === "Escape" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        ctx.setOpen(false);
        return;
      }

      if (!isShortcutHelpToggleKey(e)) return;

      e.preventDefault();
      e.stopPropagation();
      ctx.toggle();
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [ctx]);

  if (!ctx || !enabled) return null;

  const dialogId = ctx.open ? ctx.dialogId : null;
  const help = buildShortcutHelp(ctx.pathname, ctx.room, dialogId);

  return (
    <Dialog open={ctx.open} onOpenChange={ctx.setOpen}>
      <DialogContent
        className="max-w-md z-[80]"
        overlayClassName="z-[80]"
        data-testid="shortcut-help-dialog"
        data-shortcut-help-dialog=""
        aria-describedby="shortcut-help-note"
      >
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription className="sr-only">
            Keys for this window, this screen, and everywhere. Press Command slash
            or Escape to close.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {help.thisDialog.length > 0 && (
            <ShortcutGroup title="This window" items={help.thisDialog} />
          )}
          {help.thisScreen.length > 0 && (
            <ShortcutGroup title="This screen" items={help.thisScreen} />
          )}
          {help.go?.length > 0 && (
            <ShortcutGroup title="Go (G then a letter)" items={help.go} />
          )}
          <ShortcutGroup title="Every screen" items={help.everywhere} />
          <p id="shortcut-help-note" className="text-xs text-muted-foreground">
            Letter shortcuts do not run while you are typing. ⌘/ (Ctrl+/ on
            Windows) still opens this list from a field. Escape closes this list
            first when another window is open. Escape also clicks away from the
            highlighted control, the same as clicking empty page background.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShortcutGroup({ title, items }) {
  return (
    <section>
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </h3>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={`${item.keys}-${item.label}`} className="flex items-start justify-between gap-4 text-sm">
            <span className="text-foreground">{item.label}</span>
            <kbd className="shrink-0 px-1.5 py-0.5 bg-muted rounded text-muted-foreground font-mono text-xs">
              {item.keys}
            </kbd>
          </li>
        ))}
      </ul>
    </section>
  );
}
